import { Fragment, useEffect, useState } from "react";
import {
  Typography,
  useTheme,
  Box,
  Paper,
  Card,
  Chip,
  CircularProgress,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import useAlert from "../../hooks/useAlert";
import { desativaAtivaByID, getCartoes } from "../../api/CartoesCreditoCliente";

export default function ClienteCartaoCredito({ idCliente, isLoading = false }) {
  const [cartoes, setCartoes] = useState([]);
  const [shimmer, setShimmer] = useState(false);
  const { showAlertSuccess, showAlertError } = useAlert();
  const theme = useTheme();

  const init = async () => {
    setShimmer(true);
    try {
      const response = await getCartoes(idCliente);
      // Ajuste conforme a estrutura real da sua API
      setCartoes(response.data || response.data?.data || []);
    } catch (error) {
      console.error("Erro ao carregar cartões:", error);
      showAlertError(error.message || "Erro ao carregar cartões");
    } finally {
      setShimmer(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  const handlerDisable = async (idCartao) => {
    setShimmer(true);
    try {
      await desativaAtivaByID(idCliente, idCartao);
      await init(); // Recarrega a lista
      showAlertSuccess("Cartão de Crédito atualizado com sucesso.");
    } catch (error) {
      console.error("Erro ao atualizar cartão:", error);
      showAlertError(error.message || "Erro ao atualizar cartão");
    } finally {
      setShimmer(false);
    }
  };

  // Componente de Loading (Shimmer)
  const ShimmerLoading = () => (
    <Box sx={{ marginTop: 2 }}>
      {Array.from({ length: 3 }, (_, index) => (
        <Card
          key={index}
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CircularProgress size={20} />
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  width: "60%",
                  height: 20,
                  bgcolor: "grey.300",
                  borderRadius: 1,
                  mb: 1,
                }}
              />
              <Box
                sx={{
                  width: "40%",
                  height: 16,
                  bgcolor: "grey.200",
                  borderRadius: 1,
                }}
              />
            </Box>
          </Box>
        </Card>
      ))}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Cartões de Crédito
      </Typography>

      {shimmer ? (
        <ShimmerLoading />
      ) : !shimmer && cartoes.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 2,
          }}
        >
          <CreditCardIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Nenhum cartão de crédito cadastrado
          </Typography>
        </Paper>
      ) : (
        <Box>
          {cartoes.map((cartao) => {
            return (
              <Fragment
                key={cartao.id ?? `${cartao.numero}-${cartao.bandeira}`}
              >
                <Card
                  elevation={1}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    "&:hover": {
                      boxShadow: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flex: 1,
                      }}
                    >
                      <CreditCardIcon color="primary" />

                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {cartao.nomeTitular}
                          <Chip
                            style={{ marginLeft: 5 }}
                            label={cartao.ativo ? "Ativo" : "Inativo"}
                            size="small"
                            color={cartao.ativo ? "success" : "default"}
                            variant="outlined"
                          />
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontStyle: "italic" }}
                        >
                          {cartao.bandeira} -{" "}
                          {"**** **** **** " + cartao.numero?.slice(-4)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography
                        component="span"
                        onClick={() => handlerDisable(cartao.id)}
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {cartao.ativo ? "Desativar" : "Ativar"}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Fragment>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
